import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Users, Clock, BadgeCheck, AlertTriangle, UserX, DollarSign, ShieldAlert, Landmark, ExternalLink, CheckCircle2 } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className={`rounded-2xl p-5 border shadow-sm bg-white ${accent}`}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-widest font-bold text-slate-400">{label}</p>
      <Icon className="w-5 h-5 text-slate-400" />
    </div>
    <p className="mt-3 text-3xl font-black font-display text-slate-900">{value}</p>
  </div>
);

const PersonRow = ({ u, right }) => (
  <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
    <Avatar className="w-9 h-9 shrink-0">
      <AvatarImage src={u.picture} />
      <AvatarFallback className="bg-purple-700 text-white font-bold text-sm">{u.name?.[0]?.toUpperCase()}</AvatarFallback>
    </Avatar>
    <div className="min-w-0 flex-1">
      <p className="font-bold text-slate-900 truncate text-sm">{u.name}</p>
      <p className="text-xs text-slate-500 truncate">{u.email}</p>
    </div>
    {right}
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get("/admin/dashboard")
      .then(({ data }) => setData(data))
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); });
  }, []);

  if (forbidden || (user && user.role !== "owner")) {
    return (
      <div data-testid="admin-forbidden" className="text-center py-20">
        <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto" />
        <p className="mt-4 text-lg font-bold text-slate-700">Owners only</p>
        <p className="text-slate-500">You don't have access to this page.</p>
      </div>
    );
  }

  if (!data) {
    return <div className="text-slate-400 py-20 text-center animate-pulse">Loading dashboard…</div>;
  }

  const s = data.stats;
  const p = data.payouts || {};
  const live = p.stripe_mode === "live";

  return (
    <div className="space-y-8 animate-fade-up" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-purple-700 font-bold">Control center</p>
        <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight">Admin Dashboard</h1>
        <p className="mt-2 text-slate-500">Membership health and revenue at a glance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testid="stat-total" icon={Users} label="Total users" value={s.total_users} accent="border-slate-100" />
        <StatCard testid="stat-pending" icon={Clock} label="Pending" value={s.pending} accent="border-amber-200" />
        <StatCard testid="stat-active" icon={BadgeCheck} label="Active members" value={s.active_members} accent="border-purple-200" />
        <StatCard testid="stat-lapsed" icon={AlertTriangle} label="Lapsed" value={s.lapsed} accent="border-red-100" />
        <StatCard testid="stat-never" icon={UserX} label="Never paid" value={s.never_paid} accent="border-slate-100" />
        <StatCard testid="stat-collected" icon={DollarSign} label="Total collected" value={`$${s.total_collected}`} accent="border-green-200" />
      </div>

      {/* Payouts / bank */}
      <section data-testid="payouts-panel" className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-purple-700 flex items-center justify-center shrink-0">
              <Landmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-black text-slate-900">Payouts &amp; bank account</h2>
              <p className="text-sm text-slate-500">All membership payments are collected into your CFAA Stripe account.</p>
            </div>
          </div>
          <span data-testid="payout-status" className={`text-xs font-bold px-3 py-1.5 rounded-full ${live ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
            {live ? "Bank connected · Live" : "Setup required"}
          </span>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Available to pay out</p>
            <p className="mt-1 text-2xl font-black font-display text-slate-900">${s.total_collected}</p>
            <p className="text-xs text-slate-400 mt-1">Total successfully collected to date</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100 space-y-2">
            {live ? (
              <p className="text-sm text-green-700 font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Your bank is linked. Stripe pays out automatically.</p>
            ) : (
              <p className="text-sm text-slate-600">To receive this money in your bank, connect your bank account in Stripe. Funds then pay out automatically on Stripe's schedule.</p>
            )}
            <a href={p.dashboard_url || "https://dashboard.stripe.com/settings/payouts"} target="_blank" rel="noopener noreferrer"
               data-testid="connect-bank-link"
               className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold btn-lift">
              <Landmark className="w-4 h-4" /> {live ? "Manage payouts" : "Link bank in Stripe"} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
        {!live && (
          <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
            Note: payments are currently in <b>test mode</b>. To collect real money and pay out to your bank, add your own live Stripe secret key, then link your bank in the Stripe Dashboard.
          </p>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <section>
          <h2 className="font-black text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Lapsed members ({data.lapsed.length})</h2>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.lapsed.length === 0 ? <p className="text-sm text-slate-400 bg-white rounded-xl p-4 border border-slate-100">No lapsed members 🎉</p> :
              data.lapsed.map((u) => (
                <PersonRow key={u.user_id} u={u} right={<span className="text-[11px] font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full shrink-0">Lapsed</span>} />
              ))}
          </div>
        </section>

        <section>
          <h2 className="font-black text-slate-900 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-600" /> Recent payments</h2>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.recent_payments.length === 0 ? <p className="text-sm text-slate-400 bg-white rounded-xl p-4 border border-slate-100">No payments yet</p> :
              data.recent_payments.map((t) => (
                <div key={t.session_id} className="flex items-center justify-between bg-white rounded-xl p-3 border border-slate-100">
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate text-sm">{t.user_email}</p>
                    <p className="text-xs text-slate-500 truncate">{t.purpose} · {new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="font-black text-green-700 shrink-0">${t.amount}</span>
                </div>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
}
